// Client-side mirror of the BE column limits / CHECKs for procurement writes, so
// oversize or malformed values are caught before they turn into a 500.
// Format checks only run on non-empty values; required-ness stays with each form.

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const PHONE_RE = /^\+?[\d\s.\-()]{6,}$/
export const TAX_CODE_RE = /^\d{10}(-?\d{3})?$/
export const DIGITS_RE = /^\d+$/
export const URL_RE = /^https?:\/\/\S+$/i
export const CURRENCY_RE = /^[A-Z]{3}$/

const PATTERN_MESSAGE = {
  email: "Email không hợp lệ",
  phone: "Số điện thoại không hợp lệ",
  taxCode: "MST phải gồm 10 số hoặc 10-3 số",
  digits: "Chỉ được nhập số",
  url: "Link phải bắt đầu bằng http:// hoặc https://",
  currency: "Mã tiền tệ gồm 3 chữ in hoa (VD: VND)",
}

const PATTERN_RE = {
  email: EMAIL_RE,
  phone: PHONE_RE,
  taxCode: TAX_CODE_RE,
  digits: DIGITS_RE,
  url: URL_RE,
  currency: CURRENCY_RE,
}

// Spaces are visual grouping in account numbers / tax codes, not data.
const normalize = (value, pattern) => {
  const s = String(value ?? "").trim()
  return pattern === "digits" || pattern === "taxCode" ? s.replace(/\s+/g, "") : s
}

// rule = { max?, pattern?: keyof PATTERN_RE }
export const fieldError = (value, { max, pattern } = {}) => {
  const s = normalize(value, pattern)
  if (!s) return null
  if (max && s.length > max) return `Tối đa ${max} ký tự (đang ${s.length})`
  if (pattern && !PATTERN_RE[pattern].test(s)) return PATTERN_MESSAGE[pattern]
  return null
}

// Returns { [key]: message } for the keys of `rules` that fail; {} when all pass.
export const validateFields = (values, rules) =>
  Object.entries(rules).reduce((errs, [key, rule]) => {
    const msg = fieldError(values?.[key], rule)
    return msg ? { ...errs, [key]: msg } : errs
  }, {})

export const hasErrors = (errs) => Object.values(errs || {}).some(Boolean)

export const firstError = (errs) => Object.values(errs || {}).find(Boolean) || null

// Column sizes from the pro.* schema.
export const RULES = {
  orderRequest: {
    title: { max: 200 },
    region: { max: 10 },
    urgency: { max: 10 },
    paymentAssigneeEmail: { pattern: "email" },
  },
  orderRequestLine: {
    deliverContactPhone: { max: 20, pattern: "phone" },
    deliverContactName: { max: 100 },
    deliverAddress: { max: 500 },
    ticketLink: { max: 500, pattern: "url" },
    proofImageUrl: { max: 500 },
    offCatalogItemName: { max: 200 },
    offCatalogUnit: { max: 20 },
    replacedAssetCode: { max: 30 },
  },
  groupOrder: {
    title: { max: 200 },
  },
  paymentRequest: {
    batchLabel: { max: 50 },
    paymentTimeline: { max: 50 },
    bankReceiverName: { max: 200 },
    bankNameBranch: { max: 200 },
    bankAccount: { max: 100, pattern: "digits" },
    invoiceSerial: { max: 50 },
    invoiceNumber: { max: 50 },
    supplierTaxCode: { max: 50, pattern: "taxCode" },
  },
  supplier: {
    name: { max: 200 },
    taxCode: { max: 20, pattern: "taxCode" },
    email: { max: 100, pattern: "email" },
    bankName: { max: 100 },
    bankAccount: { max: 30, pattern: "digits" },
    region: { max: 10 },
  },
  vendorProposal: {
    contactEmail: { pattern: "email" },
    contactPhone: { pattern: "phone" },
  },
  publicVendorProposal: {
    taxCode: { max: 20, pattern: "taxCode" },
    address: { max: 500 },
    contactName: { max: 200 },
    contactEmail: { max: 100, pattern: "email" },
    contactPhone: { max: 30, pattern: "phone" },
  },
  item: {
    itemCode: { max: 50 },
    name: { max: 200 },
    uom: { max: 10 },
    priceCurrency: { max: 3, pattern: "currency" },
    imageUrl: { max: 500 },
  },
  itemGroup: {
    code: { max: 30 },
    name: { max: 120 },
    accountCode: { max: 30 },
    ...Object.fromEntries(Array.from({ length: 10 }, (_, i) => [`l${i + 1}Code`, { max: 50 }])),
  },
  budgetCategory: {
    name: { max: 100 },
    code: { max: 30 },
    assetClassCode: { max: 20 },
    sunPlAccount: { max: 10 },
    sunBsAccount: { max: 10 },
  },
  approvalRule: {
    name: { max: 200 },
  },
  approvalStep: {
    approverRef: { max: 200 },
    escalateTo: { max: 200 },
  },
  proposalCriteria: {
    name: { max: 200 },
  },
}

// Keyed by the OrderLineRow UI field names (OR form + DirectSourceBuilder),
// which differ from the wire names buildLinePayload maps them to.
const ORDER_LINE_UI_RULES = {
  replacedAssetCode: RULES.orderRequestLine.replacedAssetCode,
  deliveryContactName: RULES.orderRequestLine.deliverContactName,
  deliveryContactPhone: RULES.orderRequestLine.deliverContactPhone,
  deliveryAddress: RULES.orderRequestLine.deliverAddress,
  ticketLink: RULES.orderRequestLine.ticketLink,
}
const CUSTOM_ITEM_UI_RULES = {
  customItemName: RULES.orderRequestLine.offCatalogItemName,
  offCatalogUnit: RULES.orderRequestLine.offCatalogUnit,
}

export const validateOrderLine = (line) => {
  const errs = validateFields(line, line.isCustomItem ? { ...ORDER_LINE_UI_RULES, ...CUSTOM_ITEM_UI_RULES } : ORDER_LINE_UI_RULES)
  // DB CHECK: replacement ⇒ replaced_asset_code, otherwise a 500.
  if (line.type === "replacement" && !String(line.replacedAssetCode || "").trim()) {
    errs.replacedAssetCode = "Bắt buộc khi Type là Replacement"
  }
  return errs
}

// First blocking problem in an approval-rule editor draft ({ rule, conditions, steps }), or null.
// Enum fields come from dropdowns, so only free-text / numeric columns are checked.
// Mirrors APPROVER_DYNAMIC_OPTIONS in constants.js (which imports this file).
const DYNAMIC_APPROVER_CONDITION_FIELD = {
  center_manager: "center",
  department_manager: "department",
}

export const approvalRuleIssue = (draft) => {
  const rule = draft?.rule || draft || {}
  if (!String(rule.name || "").trim()) return "Rule name is required."
  const nameError = fieldError(rule.name, RULES.approvalRule.name)
  if (nameError) return `Rule name: ${nameError}`
  const badCondition = (draft?.conditions || []).findIndex((c) => !String(c.value ?? "").trim())
  if (badCondition >= 0) return `Condition ${badCondition + 1}: chọn hoặc nhập giá trị.`
  for (const [idx, step] of (draft?.steps || []).entries()) {
    const label = `Step ${idx + 1}`
    const quorum = Number(step.quorum)
    if (step.quorum != null && step.quorum !== "" && !(Number.isInteger(quorum) && quorum >= 1)) {
      return `${label}: quorum phải là số nguyên ≥ 1.`
    }
    if (step.slaHours != null && step.slaHours !== "" && !(Number(step.slaHours) > 0)) {
      return `${label}: SLA (giờ) phải lớn hơn 0.`
    }
    const msg = firstError(validateFields(step, RULES.approvalStep))
    if (msg) return `${label}: ${msg}`
    const conditionField = DYNAMIC_APPROVER_CONDITION_FIELD[step.approverType]
    const hasCondition = (draft?.conditions || []).some(
      (c) => c.field === conditionField && (c.operator === "=" || c.operator === "in")
    )
    if (conditionField && !hasCondition) {
      return `${label}: this approver needs a rule condition on ${conditionField} (= or in).`
    }
  }
  return null
}

// DB CHECK on pro.payment_request.payment_type.
export const ALLOWED_PAYMENT_TYPES = ["normal", "deposit"]

export const FISCAL_YEAR_MIN = 2000
export const FISCAL_YEAR_MAX = 2100
